import React, { useState, useEffect, useRef } from 'react';
import { Star, CheckCircle2, ChevronDown, Phone, Mail, MapPin, ArrowRight, Sparkles } from 'lucide-react';

const useInView = (threshold = 0.1) => {
const ref = useRef(null);
const [inView, setInView] = useState(false);
useEffect(() => {
const observer = new IntersectionObserver(([entry]) => {
if (entry.isIntersecting) setInView(true);
}, { threshold });
if (ref.current) observer.observe(ref.current);
return () => observer.disconnect();
}, [threshold]);
return [ref, inView];
};

const AnimatedSection = ({ children, className = '', delay = 0 }) => {
const [ref, inView] = useInView();
return (
<div ref={ref} className={className} style={{
opacity: inView ? 1 : 0,
transform: inView ? 'translateY(0)' : 'translateY(40px)',
transition: `opacity 0.7s ease ${delay}s, transform 0.7s ease ${delay}s`
}}>
{children}
</div>
);
};

const HomePage = ({ onNavigate }) => {
const [activeFaq, setActiveFaq] = useState(null);

const faqs = [
{ q: "Hur fungerar prissättning?", a: "Vi prissätter baserat på bostadsyta (kr/m²) och typ av städning. Standardstädning: 60 kr/m², Djupstädning: 72 kr/m², Flyttstädning: 85 kr/m². Med 50% RUT-avdrag betalar du bara hälften — staten betalar resten direkt till oss." },
{ q: "Varför är ni bättre än andra städfirmor?", a: "Skillnaden är enkel: Vi är inte bara städare, vi är specialister. Våra teammedlemmar har erfarenhet från högt reglerade miljöer som sjukhus och skolor. Vi tränar vårt team (vid behov) innan de får städa sitt första hem." },
{ q: "Hur fungerar RUT-avdraget?", a: "RUT-avdraget ger dig 50% rabatt på arbetskostnaden. Du betalar bara hälften — vi ansöker om resterande 50% direkt från Skatteverket. Du behöver inte göra något extra. Maxbelopp är 75 000 kr per person och år." },
{ q: "Vad händer om jag inte är nöjd?", a: "Om du inte är 100% nöjd städar vi om det utan kostnad. Inga frågor, inget krångel. Vi har råd med denna garanti eftersom vårt team håller högsta standard." }
];

const testimonials = [
{ name: "Maria Andersson", location: "Motala", rating: 5, text: "Galax är i en helt annan liga. Det var som att se ett proffsteam på jobbet - de kom, arbetade i perfekt harmoni, och lämnade mitt hem skinande rent.", service: "Djupstädning" },
{ name: "Johan Eriksson", location: "Motala", rating: 5, text: "Som fastighetsägare har jag arbetat med många städfirmor. Galax är de enda som faktiskt håller sina tidsuppskattningar OCH överträffar kvalitetsförväntningarna.", service: "Flyttstädning" },
{ name: "Lena Johansson", location: "Motala", rating: 5, text: "Teamet som kom var så professionella - matchande uniformer, tydlig kommunikation, och de städade mitt hem på rekordtid.", service: "Standardstädning" }
];

return (
<div style={{ backgroundColor: '#f8f7f4', color: '#1a1a2e' }}>

{/* HERO — synlig bild med lätt overlay */}
<section className="relative min-h-screen flex items-center justify-center overflow-hidden" style={{ paddingTop: '96px' }}>
{/* Full synlig bakgrundsbild */}
<div className="absolute inset-0" style={{
backgroundImage: `url('https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=1600&q=80')`,
backgroundSize: 'cover',
backgroundPosition: 'center'
}}/>
{/* Lätt overlay — bara 50% istället för 90% */}
<div className="absolute inset-0" style={{ backgroundColor: 'rgba(26,26,46,0.55)' }}/>

{/* Gold accent lines */}
<div className="absolute top-0 left-0 w-full h-1" style={{ background: 'linear-gradient(90deg, transparent, #C9A84C, transparent)' }}/>
<div className="absolute bottom-0 left-0 w-full h-1" style={{ background: 'linear-gradient(90deg, transparent, #C9A84C, transparent)' }}/>

{/* RUT badge */}
<div className="absolute top-28 right-6 md:right-12 z-20">
<div className="w-28 h-28 md:w-36 md:h-36 rounded-full flex flex-col items-center justify-center animate-bounce"
style={{ backgroundColor: '#C9A84C', boxShadow: '0 8px 32px rgba(201,168,76,0.6)' }}>
<p className="text-3xl md:text-4xl font-black" style={{ color: '#1a1a2e' }}>50%</p>
<p className="text-xs md:text-sm font-bold text-center leading-tight px-2" style={{ color: '#1a1a2e' }}>RUT-avdrag</p>
<p className="text-xs font-semibold" style={{ color: '#1a1a2e' }}>Nu!</p>
</div>
</div>

<div className="relative z-10 max-w-6xl mx-auto px-4 text-center text-white">
<div className="inline-flex items-center space-x-2 px-4 py-2 rounded-full mb-8 border"
style={{ borderColor: 'rgba(201,168,76,0.4)', backgroundColor: 'rgba(201,168,76,0.15)' }}>
<Sparkles className="w-4 h-4" style={{ color: '#C9A84C' }} />
<span className="text-sm tracking-widest" style={{ color: '#C9A84C' }}>MOTALAS FÖRSTA ELITE STÄDTEAM</span>
</div>

<h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
Inte Bara Städning.<br />
<span style={{ color: '#C9A84C' }}>En Upplevelse.</span>
</h1>

<p className="text-xl md:text-2xl mb-4 max-w-3xl mx-auto" style={{ color: 'rgba(255,255,255,0.9)' }}>
Ett synkroniserat team av elitspecialister med erfarenhet från sjukhus och skolor — platser där precision är kritisk.
</p>

<div className="flex flex-col sm:flex-row gap-4 justify-center mt-10 mb-16">
<button onClick={() => onNavigate('booking')}
className="px-8 py-4 rounded-full font-bold text-lg transition-all hover:scale-105"
style={{ backgroundColor: '#C9A84C', color: '#1a1a2e' }}>
Boka Städning
</button>
<button onClick={() => document.getElementById('why').scrollIntoView({ behavior: 'smooth' })}
className="px-8 py-4 rounded-full font-bold text-lg border-2 transition-all hover:scale-105"
style={{ borderColor: 'rgba(255,255,255,0.5)', color: 'white', backgroundColor: 'transparent' }}>
Se Mer ↓
</button>
</div>

<div className="flex flex-col sm:flex-row justify-center gap-6 text-sm" style={{ color: 'rgba(255,255,255,0.8)' }}>
<div className="flex items-center justify-center space-x-2">
<Phone className="w-4 h-4" style={{ color: '#C9A84C' }} />
<a href="tel:0739414366" className="hover:text-white transition-colors">073-941-43-66</a>
</div>
<div className="flex items-center justify-center space-x-2">
<Mail className="w-4 h-4" style={{ color: '#C9A84C' }} />
<a href="mailto:kontakt@galaxab.se" className="hover:text-white transition-colors">kontakt@galaxab.se</a>
</div>
<div className="flex items-center justify-center space-x-2">
<MapPin className="w-4 h-4" style={{ color: '#C9A84C' }} />
<span>Agneshögsgatan 24, Motala</span>
</div>
</div>
</div>

<div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
<ChevronDown className="w-8 h-8" style={{ color: '#C9A84C' }} />
</div>
</section>

{/* STATS BAR */}
<section style={{ backgroundColor: '#1a1a2e' }} className="py-8">
<div className="max-w-6xl mx-auto px-4">
<div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
{[
{ value: '100%', label: 'Kvalitetsgaranti' },
{ value: '6 Veckor', label: 'Elite Träning (vid behov)' },
{ value: '50%', label: 'RUT-Avdrag' },
{ value: '100%', label: 'Nöjdhet' }
].map((stat, idx) => (
<div key={idx} className="py-4">
<div className="text-3xl font-bold mb-1" style={{ color: '#C9A84C' }}>{stat.value}</div>
<div className="text-sm" style={{ color: 'rgba(255,255,255,0.6)' }}>{stat.label}</div>
</div>
))}
</div>
</div>
</section>

{/* WHY GALAX */}
<section id="why" className="py-24 px-4" style={{ backgroundColor: '#f8f7f4' }}>
<div className="max-w-6xl mx-auto">
<AnimatedSection className="text-center mb-16">
<p className="text-sm font-bold tracking-widest mb-3" style={{ color: '#C9A84C' }}>VARFÖR GALAX</p>
<h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ color: '#1a1a2e' }}>Vi Konkurrerar Inte.<br/>Vi Dominerar.</h2>
<div className="w-16 h-1 mx-auto" style={{ backgroundColor: '#C9A84C' }}/>
</AnimatedSection>

<div className="grid md:grid-cols-2 gap-12 items-center mb-20">
<AnimatedSection delay={0.1}>
<img src="https://images.unsplash.com/photo-1669101602124-f5b78895d91c?q=80&w=1170&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
alt="Professional cleaning"
className="rounded-2xl shadow-2xl w-full object-cover"
style={{ height: '400px' }}/>
</AnimatedSection>
<AnimatedSection delay={0.2}>
<h3 className="text-3xl font-bold mb-6" style={{ color: '#1a1a2e' }}>Sjukhus & Skol-Erfarenhet</h3>
<p className="text-lg mb-6" style={{ color: '#555' }}>
Våra städare har arbetat i högt reglerade miljöer där precision inte är ett val — det är ett krav. På sjukhus finns det nolltolerans för misstag. Den standarden tar vi med oss till ditt hem.
</p>
<div className="space-y-3">
{['Certifierade specialister', 'Sjukhus- och skolstandard', 'Professionell utrustning', 'Bakgrundskontrollerade'].map(item => (
<div key={item} className="flex items-center space-x-3">
<div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#C9A84C' }}>
<svg width="10" height="8" viewBox="0 0 10 8" fill="none">
<path d="M1 4L3.5 6.5L9 1" stroke="#1a1a2e" strokeWidth="2" strokeLinecap="round"/>
</svg>
</div>
<span style={{ color: '#333' }}>{item}</span>
</div>
))}
</div>
<button onClick={() => onNavigate('about')}
className="mt-8 px-6 py-3 rounded-full font-bold transition-all hover:scale-105 flex items-center space-x-2"
style={{ backgroundColor: '#1a1a2e', color: '#C9A84C', border: '2px solid #C9A84C' }}>
<span>Läs Mer Om Oss</span><ArrowRight className="w-4 h-4" />
</button>
</AnimatedSection>
</div>

<div className="grid md:grid-cols-2 gap-12 items-center">
<AnimatedSection delay={0.1} className="order-2 md:order-1">
<h3 className="text-3xl font-bold mb-6" style={{ color: '#1a1a2e' }}>Elite Teamet</h3>
<p className="text-lg mb-6" style={{ color: '#555' }}>
Varje städare genomgår intensiv träning (vid behov upp till 6 veckor). Vi arbetar som ett synkroniserat lag — ingen improvisation, endast precision.
</p>
<div className="grid grid-cols-2 gap-4">
{[
{ num: '2-4', label: 'Specialister per uppdrag' },
{ num: '40%', label: 'Snabbare än konkurrenter' },
{ num: '0', label: 'Tolerans för misstag' },
{ num: '∞', label: 'Stolthet i arbetet' }
].map((item, i) => (
<div key={i} className="p-4 rounded-xl text-center" style={{ backgroundColor: '#fff', border: '1px solid #e8e4d9' }}>
<div className="text-2xl font-bold" style={{ color: '#C9A84C' }}>{item.num}</div>
<div className="text-xs mt-1" style={{ color: '#888' }}>{item.label}</div>
</div>
))}
</div>
</AnimatedSection>
<AnimatedSection delay={0.2} className="order-1 md:order-2">
<img src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=600&q=80"
alt="Cleaning team"
className="rounded-2xl shadow-2xl w-full object-cover"
style={{ height: '400px' }}/>
</AnimatedSection>
</div>
</div>
</section>

{/* RUT SECTION */}
<section className="py-24 px-4" style={{ backgroundColor: '#1a1a2e' }}>
<div className="max-w-6xl mx-auto">
<AnimatedSection className="text-center mb-16">
<p className="text-sm font-bold tracking-widest mb-3" style={{ color: '#C9A84C' }}>RUT-AVDRAGET</p>
<h2 className="text-4xl md:text-5xl font-bold mb-4 text-white">Du Betalar Bara <span style={{ color: '#C9A84C' }}>Hälften</span></h2>
<p className="text-lg max-w-2xl mx-auto" style={{ color: 'rgba(255,255,255,0.7)' }}>
Med RUT-avdraget betalar du bara 50% av arbetskostnaden. Vi sköter resten med Skatteverket — du behöver inte göra något.
</p>
<div className="w-16 h-1 mx-auto mt-6" style={{ backgroundColor: '#C9A84C' }}/>
</AnimatedSection>

<div className="grid md:grid-cols-3 gap-8 mb-16">
{[
{ step: '1', title: 'Du bokar', desc: 'Välj tjänst och datum. Vi visar priset efter RUT direkt i bokningsformuläret.' },
{ step: '2', title: 'Du betalar 50%', desc: 'Du betalar bara hälften av arbetskostnaden. Enkelt och transparent.' },
{ step: '3', title: 'Vi hanterar resten', desc: 'Vi ansöker om de resterande 50% direkt från Skatteverket. Du behöver inte göra något!' }
].map((item, idx) => (
<AnimatedSection key={idx} delay={idx * 0.1}>
<div className="p-8 rounded-2xl text-center" style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(201,168,76,0.3)' }}>
<div className="w-14 h-14 rounded-full flex items-center justify-center font-bold text-2xl mx-auto mb-4"
style={{ backgroundColor: '#C9A84C', color: '#1a1a2e' }}>
{item.step}
</div>
<h3 className="text-xl font-bold mb-3 text-white">{item.title}</h3>
<p style={{ color: 'rgba(255,255,255,0.6)' }}>{item.desc}</p>
</div>
</AnimatedSection>
))}
</div>

<AnimatedSection>
<div className="p-8 rounded-2xl" style={{ backgroundColor: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.3)' }}>
<div className="grid md:grid-cols-2 gap-8 items-center">
<div>
<h3 className="text-2xl font-bold mb-4 text-white">Exempel: Djupstädning 80 kvm</h3>
<div className="space-y-3">
<div className="flex justify-between py-2" style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
<span style={{ color: 'rgba(255,255,255,0.7)' }}>Totalt pris (80 × 95 kr)</span>
<span className="font-bold text-white">7 600 kr</span>
</div>
<div className="flex justify-between py-2" style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
<span style={{ color: 'rgba(255,255,255,0.7)' }}>RUT-avdrag 50% (Skatteverket betalar)</span>
<span className="font-bold" style={{ color: '#4ade80' }}>-3 800 kr</span>
</div>
<div className="flex justify-between py-3 rounded-xl px-4" style={{ backgroundColor: '#C9A84C' }}>
<span className="font-bold text-xl" style={{ color: '#1a1a2e' }}>Du betalar</span>
<span className="font-bold text-2xl" style={{ color: '#1a1a2e' }}>3 800 kr</span>
</div>
</div>
</div>
<div className="space-y-4">
{[
'Gäller privatpersoner i Sverige',
'Max 75 000 kr per person och år',
'Vi sköter all administration',
'Du ser alltid priset efter RUT direkt',
'Gäller hemstädning, flytt och storstädning'
].map((text, i) => (
<div key={i} className="flex items-center gap-3">
<span style={{ color: '#C9A84C', fontWeight: 'bold', fontSize: '18px' }}>✓</span>
<span style={{ color: 'rgba(255,255,255,0.8)' }}>{text}</span>
</div>
))}
<button onClick={() => onNavigate('rut')}
className="mt-4 px-6 py-3 rounded-full font-bold transition-all hover:scale-105"
style={{ backgroundColor: '#C9A84C', color: '#1a1a2e' }}>
Läs mer om RUT →
</button>
</div>
</div>
</div>
</AnimatedSection>
</div>
</section>

{/* SERVICES */}
<section className="py-24 px-4" style={{ backgroundColor: '#fff' }}>
<div className="max-w-6xl mx-auto">
<AnimatedSection className="text-center mb-16">
<p className="text-sm font-bold tracking-widest mb-3" style={{ color: '#C9A84C' }}>VÅRA TJÄNSTER</p>
<h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ color: '#1a1a2e' }}>Priser & Paket</h2>
<p className="text-lg" style={{ color: '#666' }}>Transparent prissättning. Premium service. 50% RUT-avdrag.</p>
<div className="w-16 h-1 mx-auto mt-4" style={{ backgroundColor: '#C9A84C' }}/>
</AnimatedSection>

<div className="grid md:grid-cols-3 gap-8">
{[
{ name: 'Standardstädning', sqm: '50-80 kvm', price: '2 495', oldPrice: '4 990', pricePerSqm: '75 kr/m²', popular: false,
features: ['Alla rum inkluderade', '2-3 timmar', '2-3 specialister', 'Premium utrustning', '50% RUT-avdrag', 'Kvalitetsgaranti'] },
{ name: 'Djupstädning', sqm: '70-100 kvm', price: '3 745', oldPrice: '7 490', pricePerSqm: '95 kr/m²', popular: true,
features: ['Komplett genomgång', '4-5 timmar', 'Fullt Elite Team', 'Premium produkter', 'Fönsterputsning', '50% RUT-avdrag', 'Detaljerad checklista'] },
{ name: 'Flyttstädning', sqm: '80-120 kvm', price: '4 495', oldPrice: '8 990', pricePerSqm: '110 kr/m²', popular: false,
features: ['Garanterad godkänd', '6-8 timmar', 'Fullt Elite Team', 'Alla utrymmen', 'Ugn & kyl', '50% RUT-avdrag', 'Fotodokumentation'] }
].map((plan, idx) => (
<AnimatedSection key={idx} delay={idx * 0.1}>
<div className={`rounded-2xl overflow-hidden h-full transition-all hover:scale-105 ${plan.popular ? 'shadow-2xl' : 'shadow-md'}`}
style={{ border: plan.popular ? '2px solid #C9A84C' : '1px solid #e8e4d9', backgroundColor: plan.popular ? '#1a1a2e' : '#fff' }}>
{plan.popular && (
<div className="py-2 text-center text-sm font-bold tracking-widest" style={{ backgroundColor: '#C9A84C', color: '#1a1a2e' }}>
POPULÄRAST
</div>
)}
<div className="p-8">
<h3 className="text-2xl font-bold mb-1" style={{ color: plan.popular ? '#fff' : '#1a1a2e' }}>{plan.name}</h3>
<p className="text-sm mb-4" style={{ color: plan.popular ? 'rgba(255,255,255,0.6)' : '#888' }}>{plan.sqm}</p>
<div className="mb-1">
<span className="text-sm line-through" style={{ color: plan.popular ? 'rgba(255,255,255,0.4)' : '#aaa' }}>från {plan.oldPrice} kr</span>
</div>
<div className="flex items-baseline mb-1">
<span className="text-4xl font-bold" style={{ color: '#C9A84C' }}>från {plan.price}</span>
<span className="ml-2 text-sm" style={{ color: plan.popular ? 'rgba(255,255,255,0.6)' : '#888' }}>kr</span>
</div>
<p className="text-xs mb-6" style={{ color: plan.popular ? 'rgba(255,255,255,0.5)' : '#aaa' }}>
Du betalar efter 50% RUT-avdrag • {plan.pricePerSqm}
</p>
<ul className="space-y-3 mb-8">
{plan.features.map((f, i) => (
<li key={i} className="flex items-center space-x-2 text-sm">
<CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: '#C9A84C' }} />
<span style={{ color: plan.popular ? 'rgba(255,255,255,0.8)' : '#555' }}>{f}</span>
</li>
))}
</ul>
<button onClick={() => onNavigate('booking')}
className="w-full py-3 rounded-full font-bold transition-all hover:scale-105"
style={{ backgroundColor: plan.popular ? '#C9A84C' : '#1a1a2e', color: plan.popular ? '#1a1a2e' : '#C9A84C', border: '2px solid #C9A84C' }}>
Välj Paket
</button>
</div>
</div>
</AnimatedSection>
))}
</div>
</div>
</section>

{/* PHOTO BANNER */}
<section className="relative py-32 overflow-hidden">
<div className="absolute inset-0" style={{
backgroundImage: `url('https://images.unsplash.com/photo-1584820927498-cfe5211fd8bf?w=1600&q=80')`,
backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed'
}}/>
<div className="absolute inset-0" style={{ backgroundColor: 'rgba(26,26,46,0.75)' }}/>
<div className="relative z-10 max-w-4xl mx-auto px-4 text-center text-white">
<AnimatedSection>
<p className="text-sm font-bold tracking-widest mb-4" style={{ color: '#C9A84C' }}>VÅR GARANTI</p>
<h2 className="text-4xl md:text-5xl font-bold mb-6">100% Nöjdhet<br/>eller Pengarna Tillbaka</h2>
<p className="text-xl mb-8" style={{ color: 'rgba(255,255,255,0.8)' }}>
Vi städar om det utan kostnad om du inte är helt nöjd. Inga frågor. Inga krångel.
</p>
<button onClick={() => onNavigate('booking')}
className="px-10 py-4 rounded-full font-bold text-lg transition-all hover:scale-105"
style={{ backgroundColor: '#C9A84C', color: '#1a1a2e' }}>
Boka Nu
</button>
</AnimatedSection>
</div>
</section>

{/* TESTIMONIALS */}
<section className="py-24 px-4" style={{ backgroundColor: '#f8f7f4' }}>
<div className="max-w-6xl mx-auto">
<AnimatedSection className="text-center mb-16">
<p className="text-sm font-bold tracking-widest mb-3" style={{ color: '#C9A84C' }}>KUNDRECENSIONER</p>
<h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ color: '#1a1a2e' }}>Vad Säger Våra Kunder?</h2>
<div className="w-16 h-1 mx-auto" style={{ backgroundColor: '#C9A84C' }}/>
</AnimatedSection>
<div className="grid md:grid-cols-3 gap-8">
{testimonials.map((t, idx) => (
<AnimatedSection key={idx} delay={idx * 0.15}>
<div className="p-8 rounded-2xl h-full" style={{ backgroundColor: '#fff', border: '1px solid #e8e4d9', boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}>
<div className="flex mb-4">
{[...Array(t.rating)].map((_, i) => (
<Star key={i} className="w-5 h-5" style={{ color: '#C9A84C' }} fill="#C9A84C" />
))}
</div>
<p className="mb-6 italic" style={{ color: '#555' }}>"{t.text}"</p>
<div className="border-t pt-4" style={{ borderColor: '#e8e4d9' }}>
<p className="font-bold" style={{ color: '#1a1a2e' }}>{t.name}</p>
<p className="text-sm" style={{ color: '#C9A84C' }}>{t.location} • {t.service}</p>
</div>
</div>
</AnimatedSection>
))}
</div>
</div>
</section>

{/* FAQ */}
<section className="py-24 px-4" style={{ backgroundColor: '#fff' }}>
<div className="max-w-3xl mx-auto">
<AnimatedSection className="text-center mb-16">
<p className="text-sm font-bold tracking-widest mb-3" style={{ color: '#C9A84C' }}>FAQ</p>
<h2 className="text-4xl font-bold mb-4" style={{ color: '#1a1a2e' }}>Vanliga Frågor</h2>
<div className="w-16 h-1 mx-auto" style={{ backgroundColor: '#C9A84C' }}/>
</AnimatedSection>
<div className="space-y-4">
{faqs.map((faq, idx) => (
<AnimatedSection key={idx} delay={idx * 0.05}>
<div className="rounded-xl overflow-hidden" style={{ border: '1px solid #e8e4d9' }}>
<button onClick={() => setActiveFaq(activeFaq === idx ? null : idx)}
className="w-full p-6 text-left flex justify-between items-center transition-all"
style={{ backgroundColor: activeFaq === idx ? '#1a1a2e' : '#fff' }}>
<span className="font-semibold" style={{ color: activeFaq === idx ? '#fff' : '#1a1a2e' }}>{faq.q}</span>
<span className="text-2xl ml-4" style={{ color: '#C9A84C' }}>{activeFaq === idx ? '−' : '+'}</span>
</button>
{activeFaq === idx && (
<div className="px-6 pb-6 pt-2" style={{ backgroundColor: '#f8f7f4' }}>
<p style={{ color: '#555' }}>{faq.a}</p>
</div>
)}
</div>
</AnimatedSection>
))}
</div>
</div>
</section>

{/* CTA BOTTOM */}
<section className="py-24 px-4" style={{ backgroundColor: '#1a1a2e' }}>
<div className="max-w-4xl mx-auto text-center">
<AnimatedSection>
<svg width="60" height="60" viewBox="0 0 40 40" className="mx-auto mb-6">
<circle cx="20" cy="20" r="20" fill="#C9A84C"/>
<polygon points="20,8 23.5,16 32,17 26,23 27.5,32 20,28 12.5,32 14,23 8,17 16.5,16" fill="#1a1a2e"/>
</svg>
<h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">Redo att Uppleva Galax?</h2>
<p className="text-xl mb-10" style={{ color: 'rgba(255,255,255,0.7)' }}>
Boka din första städning idag. Vi garanterar 100% nöjdhet eller pengarna tillbaka.
</p>
<div className="flex flex-col sm:flex-row gap-4 justify-center">
<button onClick={() => onNavigate('booking')}
className="px-10 py-4 rounded-full font-bold text-lg transition-all hover:scale-105"
style={{ backgroundColor: '#C9A84C', color: '#1a1a2e' }}>
Boka Online Nu
</button>
<a href="tel:0739414366"
className="px-10 py-4 rounded-full font-bold text-lg border-2 transition-all hover:scale-105 flex items-center justify-center space-x-2"
style={{ borderColor: 'rgba(255,255,255,0.3)', color: '#fff' }}>
<Phone className="w-5 h-5" />
<span>073-941-43-66</span>
</a>
</div>
</AnimatedSection>
</div>
</section>
</div>
);
};

export default HomePage;